import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Brain, HeartHandshake, Moon, Sparkles, Video, ArrowRight } from "lucide-react";
import { SERVICES } from "@/lib/site";

const iconMap = {
  brain: Brain,
  "heart-handshake": HeartHandshake,
  moon: Moon,
  sparkles: Sparkles,
};

export default function Services() {
  return (
    <section
      id="servizi"
      data-testid="services-section"
      className="py-20 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-16">
          <span className="eyebrow" data-testid="services-eyebrow">I miei servizi</span>
          <h2
            className="font-serif-elegant mt-6 text-3xl lg:text-5xl font-light tracking-tight text-[#3D3B3A] leading-tight"
            data-testid="services-title"
          >
            Tre percorsi,<br />
            <span className="italic text-[#6B8568]">un&apos;unica visione</span>.
          </h2>
          <p className="mt-6 text-[#73706E] text-base lg:text-lg leading-relaxed">
            Ogni percorso è costruito intorno alla persona, integrando diverse
            metodologie in base ai bisogni e agli obiettivi.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-px bg-[#CFD9C5] border border-[#CFD9C5] rounded-3xl overflow-hidden">
          {SERVICES.map((s, i) => {
            const Icon = iconMap[s.icon];
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, ease: "easeOut", delay: i * 0.08 }}
                data-testid={`service-card-${s.key}`}
                className="bg-[#F6F8F3] hover:bg-[#E9EFE4] transition-colors duration-500 p-10 lg:p-14 group"
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon size={36} strokeWidth={1} className="text-[#9DB39A]" />
                  <span className="font-serif-elegant text-3xl text-[#DAE3D2] group-hover:text-[#9DB39A] transition-colors">
                    0{i + 1}
                  </span>
                </div>
                <h3
                  className="font-serif-elegant text-2xl lg:text-3xl text-[#3D3B3A] mb-4"
                  data-testid={`service-title-${s.key}`}
                >
                  {s.title}
                </h3>
                <p className="text-[#73706E] leading-relaxed text-base">{s.desc}</p>
                {s.subs && (
                  <div className="mt-7 space-y-5">
                    {s.subs.map((sub) => (
                      <div key={sub.title} data-testid={`service-sub-${s.key}-${sub.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <p className="text-sm tracking-wide text-[#6B8568] font-medium">{sub.title}</p>
                        <p className="mt-1.5 text-sm text-[#73706E] leading-relaxed">{sub.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                <Link
                  to="/prenota"
                  data-testid={`service-cta-${s.key}`}
                  className="mt-8 inline-flex items-center text-sm text-[#3D3B3A] border-b border-[#3D3B3A] hover:border-[#7E9C7B] hover:text-[#7E9C7B] pb-1 transition-colors"
                >
                  Prenota un appuntamento
                </Link>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          data-testid="online-banner"
          className="mt-16 relative overflow-hidden rounded-[2.5rem] bg-[#3D3B3A] text-white"
        >
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage:
                "url(https://images.pexels.com/photos/7605642/pexels-photo-7605642.jpeg?auto=compress&cs=tinysrgb&w=1400)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              mixBlendMode: "screen",
            }}
          />
          <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 p-10 lg:p-16 items-center">
            <div className="lg:col-span-7">
              <span
                className="eyebrow"
                style={{ color: "#9DB39A" }}
              >
                Anche online
              </span>
              <h3 className="font-serif-elegant mt-5 text-3xl lg:text-4xl font-light leading-tight">
                Le sedute possono svolgersi <br />
                <span className="italic text-[#9DB39A]">comodamente da casa</span>.
              </h3>
              <p className="mt-5 text-white/75 leading-relaxed max-w-xl">
                Se preferisci o non puoi raggiungere lo studio di Chivasso, possiamo
                lavorare insieme online con la stessa attenzione, riservatezza ed efficacia
                di una seduta in presenza.
              </p>

              <div className="mt-8 space-y-3 max-w-xl">
                <div className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9DB39A] shrink-0" />
                  <p className="text-sm text-white/80 leading-relaxed">
                    Dopo la prenotazione, <strong className="text-white">ti invierò personalmente
                    il link</strong> per accedere alla piattaforma sicura che utilizzo.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9DB39A] shrink-0" />
                  <p className="text-sm text-white/80 leading-relaxed">
                    Nessuna app da scaricare o registrazione: <strong className="text-white">basta
                    un click</strong> dal computer, tablet o smartphone.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9DB39A] shrink-0" />
                  <p className="text-sm text-white/80 leading-relaxed">
                    Connessione criptata, nel <strong className="text-white">pieno rispetto
                    del segreto professionale</strong> e del GDPR.
                  </p>
                </div>
              </div>

              <Link
                to="/prenota"
                data-testid="online-cta-prenota"
                className="mt-10 inline-flex items-center gap-2 bg-[#9DB39A] text-white hover:bg-[#7E9C7B] rounded-full px-8 py-3.5 text-sm tracking-wide transition-all"
              >
                Prenota una seduta online
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-[#9DB39A]/20 blur-2xl" />
                <div className="relative w-48 h-48 lg:w-60 lg:h-60 rounded-full bg-[#9DB39A]/15 border border-[#9DB39A]/30 flex items-center justify-center">
                  <Video size={64} strokeWidth={1} className="text-[#9DB39A]" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
